import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useMemo } from 'react';
import { inventoryApi } from '@/lib/inventoryApi';
import { parseTypedAmount, toNumber } from '@/lib/number';
import { showSuccess, showError } from '@/lib/apiErrors';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { ImportTemplatesDialog } from '@/components/inventory/ImportTemplatesDialog';
import {
  Plus,
  Upload,
  Search,
  Filter,
  Pencil,
  Trash2,
  RefreshCw,
  Package,
  X,
  Check,
} from 'lucide-react';
import { INVENTORY_CATEGORIES } from '@/types/inventory';
import type { InventoryItem, CreateItemRequest, UpdateItemRequest } from '@/types/inventory';
import type { ApiListResponse, ApiResponse } from '@/types/api';

export const Route = createFileRoute('/_app/inventory/items')({
  component: InventoryItemsPage,
});

function InventoryItemsPage() {
  const { farmId, isLoading: isFarmLoading } = useCurrentFarm();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('true');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [createFormOpen, setCreateFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);

  // Issue #132: debounce the search input 300ms so every keystroke doesn't fire
  // a request; refetch only after the user pauses typing.
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  const params = useMemo(() => {
    const p: Record<string, string> = { include_stock: 'true' };
    if (categoryFilter !== 'all') p.category = categoryFilter;
    if (activeFilter !== 'all') p.is_active = activeFilter;
    if (debouncedSearch.trim()) p.search = debouncedSearch.trim();
    return p;
  }, [categoryFilter, activeFilter, debouncedSearch]);

  const { data, isLoading, error, refetch } = useQuery<ApiListResponse<InventoryItem>>({
    queryKey: ['inventory', 'items', farmId, params],
    queryFn: () => inventoryApi.listItems(farmId!, params),
    staleTime: 30 * 1000,
    enabled: !!farmId,
  });

  const items = data?.data ?? [];

  // Create item mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateItemRequest) =>
      inventoryApi.createItem(farmId!, data),
    onSuccess: () => {
      showSuccess('Item created successfully');
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setCreateFormOpen(false);
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Failed to create item');
    },
  });

  // Update item mutation
  const updateMutation = useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: UpdateItemRequest }) =>
      inventoryApi.updateItem(farmId!, itemId, data),
    onSuccess: () => {
      showSuccess('Item updated successfully');
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setEditingItem(null);
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Failed to update item');
    },
  });

  // Delete item mutation
  const deleteMutation = useMutation({
    mutationFn: (itemId: string) => inventoryApi.deleteItem(farmId!, itemId),
    onSuccess: () => {
      showSuccess('Item deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Failed to delete item');
    },
  });

  // Pull template mutation
  const pullTemplateMutation = useMutation({
    mutationFn: (itemId: string) => inventoryApi.pullTemplate(farmId!, itemId),
    onSuccess: (response) => {
      const fields = (response as ApiResponse<{ updated_fields: string[] }>).data.updated_fields;
      showSuccess(`Template updated: ${fields.join(', ')}`);
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Failed to pull template updates');
    },
  });

  function handleDelete(item: InventoryItem) {
    if (!window.confirm(`Delete "${item.name}"? This will soft-delete the item.`)) return;
    deleteMutation.mutate(item.id);
  }

  if (isFarmLoading || isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <LoadingSpinner size={32} className="text-primary-600" />
        <p className="text-gray-500">Loading inventory items...</p>
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={error instanceof Error ? error.message : 'Failed to load items'}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search items..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Categories' },
                ...INVENTORY_CATEGORIES.map((c) => ({ value: c.value, label: c.label })),
              ]}
            />
            <Select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All' },
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Inactive' },
              ]}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Upload className="h-4 w-4" />}
            onClick={() => setImportDialogOpen(true)}
          >
            Import Templates
          </Button>
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => setCreateFormOpen(true)}
          >
            Add Item
          </Button>
        </div>
      </div>

      {/* Create form */}
      {createFormOpen && (
        <ItemForm
          onSubmit={(data) => createMutation.mutate(data as CreateItemRequest)}
          onCancel={() => setCreateFormOpen(false)}
          isLoading={createMutation.isPending}
        />
      )}

      {/* Edit form */}
      {editingItem && (
        <ItemForm
          item={editingItem}
          onSubmit={(data) =>
            updateMutation.mutate({ itemId: editingItem.id, data })
          }
          onCancel={() => setEditingItem(null)}
          isLoading={updateMutation.isPending}
        />
      )}

      {/* Items table */}
      {items.length === 0 ? (
        <Card>
          <Card.Body>
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="h-12 w-12 text-gray-300 mb-4" />
              <h3 className="font-semibold text-gray-900">No inventory items</h3>
              <p className="text-sm text-gray-500 mt-1 mb-4">
                Add items manually or import from the template catalog.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<Upload className="h-4 w-4" />}
                  onClick={() => setImportDialogOpen(true)}
                >
                  Import Templates
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => setCreateFormOpen(true)}
                >
                  Add Item
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-gray-500">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Category</th>
                  <th className="px-5 py-3 font-medium">Unit</th>
                  <th className="px-5 py-3 font-medium text-right">Reorder Pt</th>
                  <th className="px-5 py-3 font-medium text-right">Unit Cost</th>
                  <th className="px-5 py-3 font-medium text-right">Stock</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map((item) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    onEdit={() => setEditingItem(item)}
                    onDelete={() => handleDelete(item)}
                    onPullTemplate={() => pullTemplateMutation.mutate(item.id)}
                    isPulling={pullTemplateMutation.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Results summary */}
      {items.length > 0 && (
        <p className="text-xs text-gray-400">
          Showing {items.length} item{items.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* Import dialog */}
      {farmId && (
        <ImportTemplatesDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          farmId={farmId}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['inventory'] });
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Item row
// ============================================================================

interface ItemRowProps {
  item: InventoryItem;
  onEdit: () => void;
  onDelete: () => void;
  onPullTemplate: () => void;
  isPulling: boolean;
}

function ItemRow({ item, onEdit, onDelete, onPullTemplate, isPulling }: ItemRowProps) {
  const stockQty = item.stock ? toNumber(item.stock.quantity) : null;
  const unitCost = item.unit_cost != null ? toNumber(item.unit_cost) : null;
  const reorder = toNumber(item.reorder_point);

  return (
    <tr className="hover:bg-gray-50/50 transition-colors">
      <td className="px-5 py-3">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{item.name}</span>
          {!item.is_active && (
            <Badge variant="neutral" size="sm">Inactive</Badge>
          )}
          {item.template_update_available && (
            <button
              onClick={onPullTemplate}
              disabled={isPulling}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-full hover:bg-blue-100 transition-colors"
              title="Template update available - click to pull"
            >
              <RefreshCw className={`h-3 w-3 ${isPulling ? 'animate-spin' : ''}`} />
              Update
            </button>
          )}
        </div>
        {item.item_code && (
          <p className="text-xs text-gray-400 mt-0.5">{item.item_code}</p>
        )}
      </td>
      <td className="px-5 py-3 text-gray-600 capitalize">{item.category}</td>
      <td className="px-5 py-3 text-gray-600">{item.unit}</td>
      <td className="px-5 py-3 text-right text-gray-500">{reorder.toLocaleString()}</td>
      <td className="px-5 py-3 text-right text-gray-600">
        {unitCost != null ? `P${unitCost.toLocaleString()}` : '-'}
      </td>
      <td className="px-5 py-3 text-right font-medium text-gray-900">
        {stockQty != null ? stockQty.toLocaleString() : '-'}
      </td>
      <td className="px-5 py-3">
        {item.stock ? (
          <StockStatusBadge status={item.stock.status} />
        ) : (
          <Badge variant="neutral" size="sm">No stock</Badge>
        )}
      </td>
      <td className="px-5 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={onEdit}
            className="p-1.5 rounded-md text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
            title="Edit item"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            title="Delete item"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function StockStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'ok':
      return <Badge variant="ok" dot size="sm">OK</Badge>;
    case 'low':
      return <Badge variant="warning" dot size="sm">Low</Badge>;
    case 'out':
      return <Badge variant="critical" dot size="sm">Out</Badge>;
    default:
      return <Badge variant="neutral" size="sm">{status}</Badge>;
  }
}

// ============================================================================
// Item create/edit form (inline card)
// ============================================================================

interface ItemFormProps {
  item?: InventoryItem;
  onSubmit: (data: CreateItemRequest | UpdateItemRequest) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function ItemForm({ item, onSubmit, onCancel, isLoading }: ItemFormProps) {
  const [name, setName] = useState(item?.name ?? '');
  const [category, setCategory] = useState(item?.category ?? 'feed');
  const [unit, setUnit] = useState(item?.unit ?? 'kg');
  const [itemCode, setItemCode] = useState(item?.item_code ?? '');
  const [reorderPoint, setReorderPoint] = useState(
    item ? String(toNumber(item.reorder_point)) : ''
  );
  const [unitCost, setUnitCost] = useState(
    item?.unit_cost != null ? String(toNumber(item.unit_cost)) : ''
  );
  const [sku, setSku] = useState(item?.sku ?? '');

  // "12,5" is 12.5, "1,250" is 1250 (peso amounts); anything else is refused with a message.
  const reorderParsed = parseTypedAmount(reorderPoint, { thousands: true });
  const unitCostParsed = parseTypedAmount(unitCost, { thousands: true });
  const reorderValue = 'value' in reorderParsed ? reorderParsed.value : null;
  const unitCostValue = 'value' in unitCostParsed ? unitCostParsed.value : null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || 'error' in reorderParsed || 'error' in unitCostParsed) return;

    const data: CreateItemRequest | UpdateItemRequest = {
      name: name.trim(),
      category,
      unit: unit.trim() || 'kg',
      ...(itemCode.trim() && { item_code: itemCode.trim() }),
      ...(reorderValue !== null && { reorder_point: reorderValue }),
      ...(unitCostValue !== null && { unit_cost: unitCostValue }),
      ...(sku.trim() && { sku: sku.trim() }),
    };

    onSubmit(data);
  }

  return (
    <Card className="border-primary-200 bg-primary-50/30">
      <Card.Header>
        {item ? 'Edit Item' : 'New Item'}
      </Card.Header>
      <Card.Body>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Vannamei Feed 40%"
              required
            />
            <Select
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              options={INVENTORY_CATEGORIES.map((c) => ({
                value: c.value,
                label: c.label,
              }))}
            />
            <Input
              label="Unit"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              placeholder="kg, L, pcs"
            />
            <Input
              label="Item Code"
              value={itemCode}
              onChange={(e) => setItemCode(e.target.value)}
              placeholder="Optional code"
            />
            <Input
              label="Reorder Point"
              type="text"
              inputMode="decimal"
              value={reorderPoint}
              onChange={(e) => setReorderPoint(e.target.value)}
              error={'error' in reorderParsed ? reorderParsed.error : undefined}
              placeholder="0"
            />
            <Input
              label="Unit Cost (PHP)"
              type="text"
              inputMode="decimal"
              value={unitCost}
              onChange={(e) => setUnitCost(e.target.value)}
              error={'error' in unitCostParsed ? unitCostParsed.error : undefined}
              placeholder="0.00"
            />
            <Input
              label="SKU"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              placeholder="Optional SKU"
            />
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              loading={isLoading}
              leftIcon={<Check className="h-4 w-4" />}
            >
              {item ? 'Save Changes' : 'Create Item'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onCancel}
              leftIcon={<X className="h-4 w-4" />}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card.Body>
    </Card>
  );
}
